import * as React from 'react';
import { THeader } from './Header.types';
import './Header.styled.scss';
import { Badge } from '@mui/material';
import { Button, IconButton, Cart } from 'components';
import { ShoppingCart } from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router';
import { APP_ROUTES } from '../../../constants/routes';
import { CategoriesMenu } from './components/CategoriesMenu/CategoriesMenu';
import { MobileCategoriesDrawer } from './components/MobileCategoriesDrawer/MobileCategoriesDrawer';
import { useLocalCart } from '../../../hooks/useLocalCart';
import classNames from 'classnames';
import { useModals } from 'hooks/useModals';
import { MODALS } from 'store/contexts/ModalContext/modals';
import { useDeviceType } from 'hooks/useDeviceType';
import { useAppDispatch, useAppSelector } from 'store/store';
import { EUserRoles } from 'api/users/users.types';
import { logoutUserAsyncThunk } from 'store/users/users.action';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';

export const Header: THeader = () => {
  const navigate = useNavigate();
  const localize = useLocalize();
  const { data: currentUser } = useAppSelector(
    (state) => state.users.currentUser
  );

  const { isMobile, isTablet, isDesktop } = useDeviceType();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { openModal } = useModals();
  const [cartOpen, setCartOpen] = React.useState(false);
  const { itemCount: cartItemCount } = useLocalCart();

  const isPatwayActive = (path: string) => pathname === path;

  const handleCartOpen = () => {
    setCartOpen(true);
  };

  const handleCartClose = () => {
    setCartOpen(false);
  };

  const handleRegistrationOpen = () => {
    openModal(MODALS.REGISTRATION);
  };

  const handleLoginOpen = () => {
    openModal(MODALS.LOGIN);
  };

  const handleLogout = () => {
    dispatch(
      logoutUserAsyncThunk({ onSuccess: () => navigate(APP_ROUTES.HOME) })
    );
  };

  return (
    <>
      <header className="header" id="app_header">
        <div className="header__container">
          <div className="header__main-content">
            {isDesktop && (
              <>
                <Link
                  to={APP_ROUTES.HOME}
                  className={classNames(
                    'caption',
                    'header__main-content__links__link',
                    {
                      'header__main-content__links__link--active':
                        isPatwayActive(APP_ROUTES.PRODUCTS),
                    }
                  )}
                >
                  <h3 className="header__main-content__logo">PolyShop</h3>
                </Link>
                <div className="header__main-content__links">
                  <CategoriesMenu />
                  {currentUser?.role === EUserRoles.ADMIN && (
                    <Link
                      to={APP_ROUTES.ADMIN_PANEL}
                      className={classNames(
                        'caption',
                        'header__main-content__links__link',
                        {
                          'header__main-content__links__link--active':
                            isPatwayActive(APP_ROUTES.ADMIN_PANEL),
                        }
                      )}
                    >
                      Admin panel
                    </Link>
                  )}
                </div>
              </>
            )}

            {(isTablet || isMobile) && (
              <div className="header__main-content__links">
                <MobileCategoriesDrawer />
              </div>
            )}
          </div>
          <div className="header__right-content">
            {pathname !== APP_ROUTES.CHECKOUT && (
              <IconButton
                onClick={handleCartOpen}
                className="header__cart-button"
                aria-label="shopping cart"
              >
                <Badge badgeContent={cartItemCount} color="error">
                  <ShoppingCart fontSize={isDesktop ? 'medium' : 'small'} />
                </Badge>
              </IconButton>
            )}
            {isDesktop &&
              (currentUser ? (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              ) : (
                <>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleLoginOpen}
                  >
                    {localize('common.button.signin')}
                  </Button>
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={handleRegistrationOpen}
                  >
                    Sign up
                  </Button>
                </>
              ))}
          </div>
        </div>
      </header>
      <Cart open={cartOpen} onClose={handleCartClose} />
    </>
  );
};
